import React, { ReactNode } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

type CardVariant = 'card' | 'page';

interface CardProps {
  children: ReactNode;
  className?: string;
  padding?: 'sm' | 'md' | 'lg';
  variant?: CardVariant;
}

export const Card: React.FC<CardProps> = ({ children, className = '', padding = 'md', variant = 'card' }) => {
  const { darkMode } = useTheme();

  const paddingClasses = {
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
  };

  return (
    <div
      className={`
        rounded-2xl backdrop-blur-md border
        ${darkMode ? 'bg-slate-800/50 border-slate-700/30' : 'bg-white/90 border-slate-200 shadow-sm'}
        ${paddingClasses[padding]}
        ${className}
      `}
    >
      {children}
    </div>
  );
};

interface CardHeaderProps {
  title: string;
  icon?: ReactNode;
  action?: ReactNode;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  variant?: CardVariant;
}

export const CardHeader: React.FC<CardHeaderProps> = ({ title, icon, action, collapsed, onToggleCollapse, variant = 'card' }) => {
  const { darkMode } = useTheme();

  const iconBadge = icon ? (
    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg mr-2.5 ${
      darkMode ? 'bg-violet-500/20 text-violet-300' : 'bg-violet-100 text-violet-600'
    }`}>
      {icon}
    </span>
  ) : null;

  if (variant === 'page') {
    return (
      <div className={`flex justify-between items-center gap-2 pb-4 mb-4 border-b ${darkMode ? 'border-white/10' : 'border-slate-200'}`}>
        <h2 className={`text-xl font-bold flex items-center min-w-0 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
          {iconBadge}
          <span className="truncate">{title}</span>
        </h2>
        {action && <div className="flex items-center gap-1 flex-shrink-0">{action}</div>}
      </div>
    );
  }

  return (
    <div className={`flex justify-between items-center ${collapsed ? 'mb-0' : 'mb-4'}`}>
      <h2 className={`text-xl font-bold flex items-center ${darkMode ? 'text-white' : 'text-slate-800'}`}>
        {iconBadge}
        {title}
      </h2>
      <div className="flex items-center gap-1">
        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            className={`p-1.5 rounded-lg transition-all ${
              darkMode
                ? 'text-slate-400 hover:text-slate-200 hover:bg-white/10'
                : 'text-slate-500 hover:text-violet-700 hover:bg-violet-100/60'
            }`}
            title={collapsed ? 'Expand' : 'Collapse'}
          >
            {collapsed ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
        )}
        {action}
      </div>
    </div>
  );
};

interface CollapsibleContentProps {
  collapsed: boolean;
  children: ReactNode;
  variant?: CardVariant;
}

export const CollapsibleContent: React.FC<CollapsibleContentProps> = ({ collapsed, children, variant = 'card' }) => {
  if (variant === 'page') {
    return <div>{children}</div>;
  }

  return (
    <div
      className={`transition-all duration-300 overflow-hidden ${
        collapsed ? 'max-h-0 opacity-0' : 'max-h-[2000px] opacity-100'
      }`}
    >
      {children}
    </div>
  );
};

interface CardFooterProps {
  children: ReactNode;
  className?: string;
}

export const CardFooter: React.FC<CardFooterProps> = ({ children, className = '' }) => {
  const { darkMode } = useTheme();

  return (
    <div
      className={`
        pt-3 border-t
        ${darkMode ? 'border-white/10' : 'border-slate-200'}
        ${className}
      `}
    >
      {children}
    </div>
  );
};
